/**
 * Utility functions for handling language codes and internationalization
 */
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import { LanguageOption } from "@/types/Common";

/**
 * The platform UI languages, in display order. This is the single definition -
 * every language dropdown must build its options from this list.
 *
 * Keep in sync with:
 *  - the backend default list in OrganisationResourceImpl#defaultLanguages
 *  - the i18n_<code>.properties bundles in stickntrack-i18n
 *  - the Language enum in stickntrack-db (notification content languages)
 *
 * Note: "en-gb" carries no bundle of its own; it exists for date/unit formatting
 * and falls back to the "en" text.
 */
export const PLATFORM_LANGUAGE_CODES = ["en", "en-gb", "fr", "nl", "de", "es", "pt", "it"];

/**
 * The platform languages plus the "en-both" pseudo-locale, which only the
 * language-string override editor offers. Derived so the language list itself
 * stays defined in exactly one place.
 */
export const PLATFORM_LANGUAGE_CODES_WITH_EN_BOTH = PLATFORM_LANGUAGE_CODES.flatMap((code) =>
  code === "en-gb" ? [code, "en-both"] : [code]
);

/**
 * Converts a language code to the corresponding i18n key format
 * Handles cases like "en-gb" -> "language_en_gb_key"
 *
 * @param languageCode - The language code (e.g., "en", "en-gb", "fr")
 * @returns The i18n key for the language
 */
export const getLanguageKey = (languageCode: string): string => {
  const normalizedCode = languageCode.toLowerCase().replace(/-/g, "_");
  return `language_${normalizedCode}`;
};

/**
 * Gets the display label for a language code using the language object
 *
 * @param languageCode - The language code (e.g., "en", "en-gb", "fr")
 * @param language - The language object containing i18n keys
 * @returns The display label for the language, or the language code if key not found
 */
export const getLanguageLabel = (
  languageCode: string,
  language: Record<string, string>
): string => {
  const languageKey = getLanguageKey(languageCode);
  return language[languageKey] || languageCode;
};

/**
 * Creates language options for dropdowns/selects
 *
 * @param languageCodes - Array of language codes
 * @param language - The language object containing i18n keys
 * @returns Array of {value, label} objects for dropdown options
 */
export const createLanguageOptions = (
  languageCodes: string[],
  language: Record<string, string>
) => {
  return languageCodes.map((code) => ({
    value: code,
    label: getLanguageLabel(code, language),
  }));
};

/**
 * Builds the platform language dropdown options via translate(), for call sites
 * that do not hold the language map themselves. Callers that need the labels to
 * refresh when the language bundle loads should use createLanguageOptions with
 * the useLanguage() map instead.
 *
 * @param languageCodes - Codes to build options for (defaults to all platform languages)
 * @returns Array of {value, label} objects in the given order
 */
export const getPlatformLanguageOptions = (
  languageCodes: string[] = PLATFORM_LANGUAGE_CODES
): LanguageOption[] =>
  languageCodes.map((code) => ({
    value: code,
    label: translate(getLanguageKey(code) as LanguageKey),
  }));
